/**
 * Hook for face detection using face-api.js
 * 
 * Dependencies required:
 *   npm install face-api.js @vladmandic/face-api
 *   (or) yarn add face-api.js
 * 
 * Models must be available at /models directory (public folder):
 *   - tiny_face_detector_model-weights_manifest.json
 *   - face_landmark_68_model-weights_manifest.json
 *   - face_recognition_model-weights_manifest.json
 *   - face_expression_model-weights_manifest.json
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import * as faceapi from 'face-api.js';

export interface FaceDetectionResult {
  detection: faceapi.FaceDetection;
  landmarks: faceapi.FaceLandmarks68;
  descriptor: Float32Array;
  expressions: faceapi.FaceExpressions;
}

export interface UseFaceDetectionOptions {
  modelsPath?: string;
  minConfidence?: number;
}

export function useFaceDetection(options?: UseFaceDetectionOptions) {
  const { modelsPath = '/models', minConfidence = 0.5 } = options || {};

  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const loadModels = useCallback(async () => {
    if (isModelLoaded) return;

    setIsLoading(true);
    setError(null);

    try {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(modelsPath),
        faceapi.nets.faceLandmark68Net.loadFromUri(modelsPath),
        faceapi.nets.faceRecognitionNet.loadFromUri(modelsPath),
        faceapi.nets.faceExpressionNet.loadFromUri(modelsPath),
      ]);
      setIsModelLoaded(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Falha ao carregar modelos de detecção facial';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [modelsPath, isModelLoaded]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const detectFace = useCallback(async (): Promise<FaceDetectionResult | null> => {
    if (!isModelLoaded) {
      setError('Modelos não carregados');
      return null;
    }

    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      return null;
    }

    const options = new faceapi.TinyFaceDetectorOptions({
      inputSize: 320,
      scoreThreshold: minConfidence,
    });

    const result = await faceapi
      .detectSingleFace(video, options)
      .withFaceLandmarks()
      .withFaceExpressions()
      .withFaceDescriptor();

    if (!result) {
      return null;
    }

    return {
      detection: result.detection,
      landmarks: result.landmarks,
      descriptor: result.descriptor,
      expressions: result.expressions,
    };
  }, [isModelLoaded, minConfidence]);

  const startVideo = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Falha ao acessar câmera';
      setError(message);
    }
  }, []);

  const stopVideo = useCallback(() => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  return {
    videoRef,
    canvasRef,
    isModelLoaded,
    isLoading,
    error,
    detectFace,
    startVideo,
    stopVideo,
    loadModels,
  };
}
